// datadef 참조 모델 — 작성폼 table 위젯 칼럼 해소(사본 우선 + fieldDisplay 표시 오버라이드).
// 결정: docs/decisions/2026-08-07-datadef-reference-model-design.md

import type {
  DataDef,
  DataDefField,
  InputWidget,
  TableFieldDisplayOverride,
} from '@safetyhub/shared';

/**
 * 표시 오버라이드 1건을 칼럼 정본에 병합 — **표시 축이 늘면 고칠 유일한 곳**.
 * 지정된 축만 덮고(undefined는 덮지 않음), 덮을 게 없으면 원본 필드 참조를 그대로 돌려준다.
 * 선언 축(key/label/widget/options 등 칼럼 구성)은 정본 소유라 여기서 다루지 않는다.
 */
export function applyFieldDisplayOverride(
  field: DataDefField,
  override: TableFieldDisplayOverride | undefined,
): DataDefField {
  if (!override) return field;
  const merged: DataDefField = { ...field };
  if (override.align !== undefined) merged.align = override.align;
  if (override.collapseLines !== undefined) merged.collapseLines = override.collapseLines;
  return merged;
}

/**
 * 작성폼 table 위젯의 칼럼(DataDefField[]) 해소 — 렌더가 읽는 단일 정본.
 *
 * 해소 규칙(**사본 우선**): `node.fields ?? 정본(dataDefs에서 bindingKey 매칭).fields ?? []`.
 * - 사본 존재 = 동결: 구 스냅샷·카탈로그 스캐폴드 표(대응 dataDef 없음)·미정규화 템플릿.
 *   P3 정규화 전까지 전 템플릿이 사본을 가지므로 이 헬퍼 도입만으로는 **동작 불변**이다.
 * - 사본 부재 = 정본 live 참조: 데이터 탭에서 정본을 고치면 그 데이터셋을 쓰는 전 배치에 반영.
 *
 * 그 위에 `node.fieldDisplay`(배치본 표시 오버라이드)를 필드 key별로 `applyFieldDisplayOverride`로
 * 병합한다 — 지정된 키만 덮고, 미지정 필드는 그대로 통과. fieldDisplay가 없으면 해소된
 * **원본 배열 참조를 그대로 반환**한다(불필요한 할당 절감).
 *
 * 결정: docs/decisions/2026-08-07-datadef-reference-model-design.md
 */
export function resolveTableFields(
  node: Pick<InputWidget, 'bindingKey' | 'fields' | 'fieldDisplay'>,
  dataDefs?: DataDef[],
): DataDefField[] {
  const base = node.fields ?? dataDefs?.find((d) => d.key === node.bindingKey)?.fields ?? [];
  const display = node.fieldDisplay;
  if (!display) return base;
  return base.map((f) => applyFieldDisplayOverride(f, display[f.key]));
}
